import React, { useState, useEffect, useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";
import clsx from "clsx";
import SideNavigation from "../common/SideNavigation";
import BottomNavigation from "../common/BottomNavigation";
import { isChatWorkspacePath } from "./appShellLayout";

const TOAST_NAV_CLEARANCE = "var(--bottom-nav-clearance, 112px)";
const TOAST_NO_NAV_CLEARANCE = "max(env(safe-area-inset-bottom, 0px), 22px)";

// Simple hook that tells whether the viewport is at least 1024 px wide (Tailwind “lg” breakpoint)
function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== "undefined" ? window.innerWidth >= 1024 : false
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(min-width: 1024px)");
    const handler = (e) => setIsDesktop(e.matches);
    if (mq.addEventListener) {
      mq.addEventListener("change", handler);
    } else {
      mq.addListener(handler);
    }
    return () => {
      if (mq.removeEventListener) {
        mq.removeEventListener("change", handler);
      } else {
        mq.removeListener(handler);
      }
    };
  }, []);

  return isDesktop;
}

export default function AppShell({
  children,
  bottomTheme = "dark",    // mobile BottomNavigation
  sideTheme = "dark",      // desktop SideNavigation + light shop surfaces
  innerFactor = 0.6,
  disableMaxWidth = false,
  disableMobileBottomPadding = false,
  hideBottomNavigation = false,
  showBottomNavScrim = true,
  allowScrollWhenBottomNavHidden = false,
  isGuest = false,
  /**
   * contentVariant:
   * - "default": let page/mobile platform surfaces own the desktop background
   * - "darkSolid": force solid black background inside shell
   */
  contentVariant = "default",
}) {
  const cssVar = {
    "--inner-max-w": disableMaxWidth ? "none" : `calc(var(--content-max-w) * ${innerFactor})`,
    ...(disableMaxWidth ? { "--content-max-w": "none" } : {}),
  };

  const isDesktop = useIsDesktop();
  const location = useLocation();
  const authActive = location.pathname === "/auth";
  const skipBottomNav = hideBottomNavigation || isGuest;
  const bottomNavPresent = !isDesktop && !skipBottomNav;

  useLayoutEffect(() => {
    if (typeof document === "undefined") return undefined;

    const targets = [document.documentElement, document.body].filter(Boolean);
    const snapshots = targets.map((target) => ({
      target,
      bottomNavPresent: target.style.getPropertyValue("--bottom-nav-present"),
      toastBottomClearance: target.style.getPropertyValue("--toast-bottom-clearance"),
    }));

    targets.forEach((target) => {
      target.style.setProperty("--bottom-nav-present", bottomNavPresent ? "1" : "0");
      target.style.setProperty(
        "--toast-bottom-clearance",
        bottomNavPresent ? TOAST_NAV_CLEARANCE : TOAST_NO_NAV_CLEARANCE
      );
    });

    return () => {
      snapshots.forEach(({ target, bottomNavPresent: previousPresent, toastBottomClearance }) => {
        if (previousPresent) {
          target.style.setProperty("--bottom-nav-present", previousPresent);
        } else {
          target.style.removeProperty("--bottom-nav-present");
        }

        if (toastBottomClearance) {
          target.style.setProperty("--toast-bottom-clearance", toastBottomClearance);
        } else {
          target.style.removeProperty("--toast-bottom-clearance");
        }
      });
    };
  }, [bottomNavPresent]);

  useEffect(() => {
    if (typeof document === "undefined") return;

    const isEditableTarget = (el) => {
      if (!el) return false;
      const tag = (el.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return true;
      if (el.isContentEditable) return true;
      // Some components wrap inputs; walk up a bit
      let cur = el;
      for (let i = 0; i < 3 && cur; i++) {
        if (cur.isContentEditable) return true;
        const t = (cur.tagName || "").toLowerCase();
        if (t === "input" || t === "textarea" || t === "select") return true;
        cur = cur.parentElement;
      }
      return false;
    };

    const applyClass = (on) => {
      try {
        document.documentElement.classList.toggle("kbd-open", on);
        document.body.classList.toggle("kbd-open", on);
      } catch {}
    };

    const syncFromActiveElement = () => {
      const ae = document.activeElement;
      const on = isEditableTarget(ae);
      applyClass(on);
    };

    // Auth uses the browser's native focus/viewport behavior. Applying the
    // global keyboard lock here makes Safari fight the auth workspace scroll
    // and was the source of the duplicated, stale keyboard layouts.
    if (authActive) {
      applyClass(false);
      return () => applyClass(false);
    }

    const onFocusIn = (e) => {
      if (!isEditableTarget(e.target)) return;
      applyClass(true);
    };

    const onFocusOut = () => {
      // Important for iOS emoji switch: focus can briefly wobble.
      // Re-check the active element on next tick.
      window.setTimeout(() => {
        syncFromActiveElement();
      }, 50);
    };

    document.addEventListener("focusin", onFocusIn, true);
    document.addEventListener("focusout", onFocusOut, true);

    // Initial sync (e.g., returning back to a page with focused input)
    window.setTimeout(syncFromActiveElement, 0);

    return () => {
      document.removeEventListener("focusin", onFocusIn, true);
      document.removeEventListener("focusout", onFocusOut, true);
      applyClass(false);
    };
  }, [authActive, location.pathname]);

  if (isDesktop) {
    // ───── Desktop only ─────
    return (
      <div
        className={clsx(
          "hidden lg:fixed lg:inset-0 lg:flex overflow-hidden",
          sideTheme === "light" ? "bg-[#0073FF]" : "bg-[#3a4450]"
        )}
      >
        <div className="relative flex h-full w-full p-[15px]" style={cssVar}>
          <SideNavigation theme={sideTheme} isGuest={isGuest} className="relative z-[80]" />
          <div
            id="app-workspace"
            className={clsx(
              "flex-1 flex flex-col overflow-hidden rounded-2xl relative z-10 pointer-events-auto",
              sideTheme === "light"
                ? "bg-white"
                : contentVariant === "darkSolid"
                ? "bg-black"
                : "vg-platform-background"
            )}
          >
            <div
              id="app-workspace-onboarding-anchor"
              className="pointer-events-none absolute left-1/2 top-1/2 h-px w-px -translate-x-1/2 -translate-y-1/2"
              aria-hidden="true"
            />
            <div
              className={clsx(
                "flex-1 lg:h-full min-h-0 page-content",
                hideBottomNavigation ? "overflow-hidden" : "overflow-auto"
              )}
            >
              <div className="page-inner h-full">{children}</div>
            </div>
          </div>
          <div
            id="modal-root"
            className="pointer-events-none absolute bottom-[15px] left-[calc(5rem+15px)] right-[15px] top-[15px] z-[13000] overflow-hidden rounded-2xl [&>*]:pointer-events-auto [&>.pointer-events-none]:pointer-events-none"
          />
        </div>
      </div>
    );
  }

  // ───── Mobile only ─────
  const isChatRoute = isChatWorkspacePath(location.pathname);
  const scrollableWorkspace = allowScrollWhenBottomNavHidden && skipBottomNav;

  return (
    <>
      <div
        className={clsx(
          "flex flex-col vg-platform-background overscroll-none",
          "h-[100dvh]"
        )}
        style={cssVar}
      >
        <div
          id="app-workspace"
          className={clsx(
            "relative",
            authActive
              ? "flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain page-content"
              : isChatRoute
              ? "flex-1 overflow-hidden min-h-0 page-content"
              : scrollableWorkspace
              ? "flex-1 overflow-auto min-h-0 page-content"
              : disableMobileBottomPadding || skipBottomNav
              ? "flex-1 min-h-0"
              : "flex-1 overflow-auto min-h-0 page-content"
          )}
          style={
            authActive || isChatRoute
              ? undefined
              : disableMobileBottomPadding || skipBottomNav
              ? undefined
              : {
                  paddingBottom: "var(--bottom-nav-clearance, 112px)",
                  scrollPaddingBottom: "var(--bottom-nav-clearance, 112px)",
                }
          }
        >
          <div
            id="app-workspace-onboarding-anchor"
            className="pointer-events-none absolute left-1/2 top-1/2 h-px w-px -translate-x-1/2 -translate-y-1/2"
            aria-hidden="true"
          />
          {children}
        </div>
        {skipBottomNav ? null : (
          <BottomNavigation
            theme={bottomTheme}
            isGuest={isGuest}
            showScrim={showBottomNavScrim}
          />
        )}
      </div>
      <div
        id="modal-root"
        className="pointer-events-none fixed inset-0 z-[13000] [&>*]:pointer-events-auto [&>.pointer-events-none]:pointer-events-none"
      />
    </>
  );
}
